import React, { useState, useEffect, Fragment, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { notificationAPI } from '../../services/api';
import { NOTIFICATION_EVENT_TYPES } from '../../constants/notificationEventTypes';
import { sanitizeHtml } from '../../utils/sanitize';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

interface NotificationRule {
  id: string;
  name: string;
  description?: string;
  eventType: string;
  entityType: string;
  templateId: string;
  conditions?: any;
  recipientTypes: string[] | null;
  customEmails: string[] | null;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
  EmailTemplate?: {
    id: string;
    name: string;
  };
}

interface EmailTemplate {
  id: string;
  name: string;
  eventType: string;
  entityType: string;
}

// Use shared event types to keep in sync with Email Templates
const EVENT_TYPES = NOTIFICATION_EVENT_TYPES;

// Entity types for filtering
const ENTITY_TYPES = [
  { value: 'CASE', label: 'Case' },
  { value: 'INCIDENT', label: 'Incident' },
  { value: 'PROBLEM', label: 'Problem' },
  { value: 'CHANGE', label: 'Change' },
  { value: 'SERVICE_REQUEST', label: 'Service Request' },
  { value: 'ALERT', label: 'Alert' },
  { value: 'KNOWLEDGE', label: 'Knowledge' },
  { value: 'TIME_ENTRY', label: 'Time Entry' },
];

const RECIPIENT_TYPES = [
  { value: 'ASSIGNEE', label: 'Assignee' },
  { value: 'ASSIGNED_TO', label: 'Assigned To' },
  { value: 'CALLER', label: 'Caller' },
  { value: 'CONTACT', label: 'Contact' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'CONTACT_EMAILS', label: 'Contact Emails (Awaiting Info)' },
  { value: 'GROUP_MEMBERS', label: 'Assignment Group Members' },
  { value: 'CREATED_BY', label: 'Created By' },
  { value: 'RESOLVED_BY', label: 'Resolved By' },
  { value: 'CLOSED_BY', label: 'Closed By' },
  { value: 'KB_OWNER', label: 'KB Owner' },
  { value: 'AUTHOR', label: 'Author' },
  { value: 'OWNER', label: 'Owner (Time Entry)' },
  { value: 'CUSTOM', label: 'Custom Email Addresses' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ACTIVE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const NotificationRulesManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const filterEventType = searchParams.get('eventType') ?? '';
  const filterEntityType = searchParams.get('entityType') ?? '';
  const filterActive = searchParams.get('active') ?? 'all';
  const filterName = searchParams.get('name') ?? '';
  const filterTemplate = searchParams.get('template') ?? '';
  const filterRecipient = searchParams.get('recipient') ?? '';

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '10', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 10;
  }, [searchParams]);

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [allRules, setAllRules] = useState<NotificationRule[]>([]);

  // Create/Edit Modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventType: '',
    entityType: '',
    templateId: '',
    recipientTypes: [] as string[],
    customEmails: [] as string[],
    isActive: true,
    priority: 0,
  });
  const [customEmailInput, setCustomEmailInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete Confirmation Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRule, setDeletingRule] = useState<NotificationRule | null>(null);

  // Test Rule Modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [testingRule, setTestingRule] = useState<NotificationRule | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Reset event type when entity type changes and current selection is not valid
  useEffect(() => {
    if (filterEntityType && filterEventType) {
      const eventType = EVENT_TYPES.find((et) => et.value === filterEventType);
      if (eventType && eventType.entityType !== filterEntityType) {
        patchSearchParams({ eventType: null, page: '1' });
      }
    }
  }, [filterEntityType, filterEventType, patchSearchParams]);

  useEffect(() => {
    fetchRules();
    fetchTemplates();
  }, [filterEventType, filterEntityType, filterActive]);

  const eventTypeFilterOptions = useMemo(() => {
    let filtered = EVENT_TYPES;
    if (filterEntityType) {
      filtered = filtered.filter((et) => et.entityType === filterEntityType);
    }
    const seen = new Map<string, string>();
    for (const et of filtered) {
      if (!seen.has(et.value)) seen.set(et.value, et.label);
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [filterEntityType]);

  const entityTypeFilterOptions = useMemo(
    () => ENTITY_TYPES.map((et) => ({ value: et.value, label: et.label })),
    []
  );

  const recipientFilterOptions = useMemo(
    () => RECIPIENT_TYPES.map((rt) => ({ value: rt.value, label: rt.label })),
    []
  );

  const filteredRules = useMemo(() => {
    let result = allRules;
    if (filterName.trim()) {
      const q = filterName.trim().toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.description && r.description.toLowerCase().includes(q))
      );
    }
    if (filterTemplate.trim()) {
      const q = filterTemplate.trim().toLowerCase();
      result = result.filter((r) => (r.EmailTemplate?.name || '').toLowerCase().includes(q));
    }
    if (filterRecipient) {
      result = result.filter((r) => {
        if (filterRecipient === 'CUSTOM') {
          return (r.customEmails || []).length > 0;
        }
        return (r.recipientTypes || []).includes(filterRecipient);
      });
    }
    return result;
  }, [allRules, filterName, filterTemplate, filterRecipient]);

  const totalCount = filteredRules.length;
  const rules = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredRules.slice(startIndex, startIndex + pageSize);
  }, [filteredRules, page, pageSize]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterEventType) params.eventType = filterEventType;
      if (filterEntityType) params.entityType = filterEntityType;
      if (filterActive !== 'all') params.isActive = filterActive === 'active';

      const response = await notificationAPI.rules.list(params);
      const rules = response.data.data || response.data || [];
      setAllRules(rules);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to fetch rules');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await notificationAPI.templates.list({ isActive: true });
      setTemplates(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleCreateNew = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      eventType: '',
      entityType: '',
      templateId: '',
      recipientTypes: [],
      customEmails: [],
      isActive: true,
      priority: 0,
    });
    setCustomEmailInput('');
    setShowFormModal(true);
  };

  const handleEdit = (rule: NotificationRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      eventType: rule.eventType,
      entityType: rule.entityType,
      templateId: rule.templateId,
      recipientTypes: rule.recipientTypes || [],
      customEmails: rule.customEmails || [],
      isActive: rule.isActive,
      priority: rule.priority,
    });
    setCustomEmailInput('');
    setShowFormModal(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.eventType || !formData.templateId) {
      showError('Please fill in all required fields');
      return;
    }

    if (formData.recipientTypes.length === 0 && formData.customEmails.length === 0) {
      showError('Please select at least one recipient type or add custom email addresses');
      return;
    }

    try {
      setSaving(true);
      if (editingRule) {
        await notificationAPI.rules.update(editingRule.id, formData);
        showSuccess('Rule updated successfully');
      } else {
        await notificationAPI.rules.create(formData);
        showSuccess('Rule created successfully');
      }
      setShowFormModal(false);
      fetchRules();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to save rule');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingRule) return;

    try {
      await notificationAPI.rules.delete(deletingRule.id);
      showSuccess('Rule deleted successfully');
      setShowDeleteModal(false);
      setDeletingRule(null);
      fetchRules();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to delete rule');
      console.error('Error:', error);
    }
  };

  const handleTestRule = async (rule: NotificationRule) => {
    setTestingRule(rule);
    setTestResult(null);
    setShowTestModal(true);
    setTestLoading(true);

    try {
      // Sample data for testing
      const sampleData = {
        case: {
          id: 'sample-case-id',
          number: 'CASE-12345',
          shortDescription: 'Sample case description',
          priority: 'high',
          state: 'open',
          category: 'Technical Support',
        },
        caller: {
          id: 'sample-caller-id',
          firstName: 'John',
          lastName: 'Doe',
          email: 'john.doe@example.com',
        },
        assignedTo: {
          id: 'sample-assignee-id',
          firstName: 'Jane',
          lastName: 'Smith',
          email: 'jane.smith@example.com',
        },
        assignmentGroup: {
          id: 'sample-group-id',
          name: 'IT Support',
          email: 'it-support@example.com',
          members: [
            { id: '1', email: 'member1@example.com', firstName: 'Member', lastName: 'One' },
            { id: '2', email: 'member2@example.com', firstName: 'Member', lastName: 'Two' },
          ],
        },
        customer: {
          id: 'sample-customer-id',
          accountName: 'Acme Corporation',
          emailAddress: 'contact@acme.com',
        },
      };

      const response = await notificationAPI.rules.test(rule.id, sampleData);
      setTestResult(response.data.data);
    } catch (error: any) {
      showError('Failed to test rule');
      console.error('Error:', error);
    } finally {
      setTestLoading(false);
    }
  };

  const handleRecipientTypeToggle = (type: string) => {
    const isSelected = formData.recipientTypes.includes(type);
    if (isSelected) {
      setFormData({
        ...formData,
        recipientTypes: formData.recipientTypes.filter(t => t !== type),
      });
    } else {
      setFormData({
        ...formData,
        recipientTypes: [...formData.recipientTypes, type],
      });
    }
  };

  const handleAddCustomEmail = () => {
    const email = customEmailInput.trim();
    if (email && !formData.customEmails.includes(email)) {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        showError('Invalid email address');
        return;
      }
      setFormData({
        ...formData,
        customEmails: [...formData.customEmails, email],
      });
      setCustomEmailInput('');
    }
  };

  const handleRemoveCustomEmail = (email: string) => {
    setFormData({
      ...formData,
      customEmails: formData.customEmails.filter(e => e !== email),
    });
  };

  const showSuccess = (message: string) => {
    setNotification({ message, type: 'success' });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message: string) => {
    setNotification({ message, type: 'error' });
    setTimeout(() => setNotification(null), 5000);
  };

  const getAvailableTemplates = () => {
    if (!formData.eventType) return [];
    return templates.filter(t => t.eventType === formData.eventType);
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'name':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={ACTIVE_FILTER_OPTIONS}
              value={filterActive}
              placeholder="All Statuses"
              active={filterActive !== 'all'}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : 'all';
                patchSearchParams({ active: val === 'all' ? null : val, page: '1' });
              }}
            />
          );
        case 'event':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={eventTypeFilterOptions}
              value={filterEventType}
              placeholder="All Events"
              onChange={(v) => {
                patchSearchParams({ eventType: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'entity':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={entityTypeFilterOptions}
              value={filterEntityType}
              placeholder="All Entities"
              onChange={(v) => {
                patchSearchParams({
                  entityType: typeof v === 'string' && v ? v : null,
                  eventType: null,
                  page: '1',
                });
              }}
            />
          );
        case 'template':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterTemplate}
              placeholder="Filter by template..."
              onChange={(v) => {
                patchSearchParams({ template: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'recipients':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={recipientFilterOptions}
              value={filterRecipient}
              placeholder="All Recipients"
              onChange={(v) => {
                patchSearchParams({ recipient: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterName,
      filterEventType,
      filterEntityType,
      filterTemplate,
      filterActive,
      filterRecipient,
      eventTypeFilterOptions,
      entityTypeFilterOptions,
      recipientFilterOptions,
      patchSearchParams,
    ]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (filterName) {
      chips.push({ id: 'name', label: 'Name', value: filterName, color: 'blue' });
    }
    if (filterEntityType) {
      const label = ENTITY_TYPES.find((e) => e.value === filterEntityType)?.label || filterEntityType;
      chips.push({ id: 'entityType', label: 'Entity', value: label, color: 'teal' });
    }
    if (filterEventType) {
      const label = EVENT_TYPES.find((e) => e.value === filterEventType)?.label || filterEventType;
      chips.push({ id: 'eventType', label: 'Event', value: label, color: 'cyan' });
    }
    if (filterTemplate) {
      chips.push({ id: 'template', label: 'Template', value: filterTemplate, color: 'blue' });
    }
    if (filterRecipient) {
      const label = RECIPIENT_TYPES.find((r) => r.value === filterRecipient)?.label || filterRecipient;
      chips.push({ id: 'recipient', label: 'Recipient', value: label, color: 'orange' });
    }
    if (filterActive !== 'all') {
      chips.push({
        id: 'active',
        label: 'Status',
        value: filterActive === 'active' ? 'Active' : 'Inactive',
        color: 'purple',
      });
    }
    return chips;
  }, [filterName, filterEntityType, filterEventType, filterTemplate, filterRecipient, filterActive]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'name':
        patchSearchParams({ name: null, page: '1' });
        break;
      case 'entityType':
        patchSearchParams({ entityType: null, eventType: null, page: '1' });
        break;
      case 'eventType':
        patchSearchParams({ eventType: null, page: '1' });
        break;
      case 'template':
        patchSearchParams({ template: null, page: '1' });
        break;
      case 'recipient':
        patchSearchParams({ recipient: null, page: '1' });
        break;
      case 'active':
        patchSearchParams({ active: null, page: '1' });
        break;
    }
  };

  const handleClearAllFilters = () => {
    patchSearchParams({
      name: null,
      entityType: null,
      eventType: null,
      template: null,
      recipient: null,
      active: null,
      page: '1',
    });
  };

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Notification Rules Management</h1>
              <p className="text-sm text-gray-600">
                Configure rules that determine when and to whom notifications are sent.
              </p>
            </div>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Create Rule
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-2 p-3 rounded-md flex-shrink-0 ${
          notification.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`text-sm ${
            notification.type === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {notification.message}
          </p>
        </div>
      )}

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      {/* Rules Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : rules.length === 0 ? (
            <div className="text-center py-12">
              <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No rules found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new notification rule.
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('name', 'Name')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('status', 'Status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('event', 'Event')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('entity', 'Entity')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('template', 'Template')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    {renderColumnHeader('recipients', 'Recipients')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 line-clamp-1">{rule.name}</div>
                    {rule.description && (
                      <div className="text-xs text-gray-500 line-clamp-1">{rule.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {rule.isActive ? (
                      <span className="px-2 py-0.5 inline-flex items-center text-xs font-medium rounded-full bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        <XCircleIcon className="h-3.5 w-3.5 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 line-clamp-2">
                      {EVENT_TYPES.find(et => et.value === rule.eventType)?.label || rule.eventType}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 inline-flex text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {ENTITY_TYPES.find(et => et.value === rule.entityType)?.label || rule.entityType}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 line-clamp-1">{rule.EmailTemplate?.name || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(rule.recipientTypes || []).slice(0, 2).map(type => (
                        <span
                          key={type}
                          className="px-1.5 py-0.5 inline-flex text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                        >
                          {RECIPIENT_TYPES.find(rt => rt.value === type)?.label || type}
                        </span>
                      ))}
                      {(rule.recipientTypes || []).length > 2 && (
                        <span className="px-1.5 py-0.5 inline-flex text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          +{(rule.recipientTypes || []).length - 2}
                        </span>
                      )}
                      {(rule.customEmails || []).length > 0 && (
                        <span className="px-1.5 py-0.5 inline-flex text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                          {(rule.customEmails || []).length} email
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleTestRule(rule)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Test Rule"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(rule)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingRule(rule);
                          setShowDeleteModal(true);
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>

        {/* Enhanced Pagination */}
        {!loading && rules.length > 0 && (
          <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
            {/* Left: Records per page dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  patchSearchParams({ pageSize: e.target.value, page: '1' });
                }}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {PAGE_SIZE_OPTIONS.map((size, index) => {
                  const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                  const isDisabled = previousSize >= totalCount && size > totalCount;
                  return (
                    <option key={size} value={size} disabled={isDisabled}>
                      {size}
                    </option>
                  );
                })}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            {/* Center: Entry count */}
            <div className="text-sm text-gray-700">
              Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
            </div>

            {/* Right: Pagination buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => patchSearchParams({ page: '1' })}
                disabled={page === 1}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                &laquo;
              </button>
              <button
                onClick={() => patchSearchParams({ page: String(Math.max(1, page - 1)) })}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page numbers */}
              {(() => {
                const totalPages = Math.ceil(totalCount / pageSize);
                const pages: (number | string)[] = [];

                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  if (page <= 4) {
                    for (let i = 1; i <= 5; i++) pages.push(i);
                    pages.push('...');
                    pages.push(totalPages);
                  } else if (page >= totalPages - 3) {
                    pages.push(1);
                    pages.push('...');
                    for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    pages.push('...');
                    for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                    pages.push('...');
                    pages.push(totalPages);
                  }
                }

                return pages.map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-3 py-1 text-sm text-gray-500">
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => patchSearchParams({ page: String(p) })}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === p
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                );
              })()}

              <button
                onClick={() => patchSearchParams({ page: String(page + 1) })}
                disabled={page >= Math.ceil(totalCount / pageSize)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => patchSearchParams({ page: String(Math.ceil(totalCount / pageSize)) })}
                disabled={page >= Math.ceil(totalCount / pageSize)}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Transition appear show={showFormModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowFormModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {editingRule ? 'Edit Notification Rule' : 'Create Notification Rule'}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowFormModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Rule Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="e.g., Notify Assignee on Case Creation"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Priority
                        </label>
                        <input
                          type="number"
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="0"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Higher priority rules are processed first
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Brief description of this rule"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Event Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.eventType}
                          onChange={(e) => {
                            const selected = EVENT_TYPES.find(et => et.value === e.target.value);
                            setFormData({
                              ...formData,
                              eventType: e.target.value,
                              entityType: selected?.entityType || '',
                              templateId: '', // Reset template when event type changes
                            });
                          }}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        >
                          <option value="">Select event type</option>
                          {EVENT_TYPES.map(et => (
                            <option key={et.value} value={et.value}>{et.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email Template <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.templateId}
                          onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={!formData.eventType}
                        >
                          <option value="">Select template</option>
                          {getAvailableTemplates().map(template => (
                            <option key={template.id} value={template.id}>{template.name}</option>
                          ))}
                        </select>
                        {formData.eventType && getAvailableTemplates().length === 0 && (
                          <p className="mt-1 text-xs text-amber-600">
                            No active templates found for this event type
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recipient Types <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {RECIPIENT_TYPES.filter(rt => rt.value !== 'CUSTOM').map(type => (
                          <div key={type.value} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.recipientTypes.includes(type.value)}
                              onChange={() => handleRecipientTypeToggle(type.value)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-900">
                              {type.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Email Addresses
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={customEmailInput}
                          onChange={(e) => setCustomEmailInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddCustomEmail();
                            }
                          }}
                          className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="email@example.com"
                        />
                        <button
                          type="button"
                          onClick={handleAddCustomEmail}
                          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Add
                        </button>
                      </div>
                      {formData.customEmails.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {formData.customEmails.map(email => (
                            <span
                              key={email}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                            >
                              {email}
                              <button
                                type="button"
                                onClick={() => handleRemoveCustomEmail(email)}
                                className="ml-2 text-blue-600 hover:text-blue-900"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Active (rule will be evaluated when the event occurs)
                      </label>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowFormModal(false)}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-300"
                    >
                      {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Test Rule Modal */}
      <Transition appear show={showTestModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowTestModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Test Rule: {testingRule?.name}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowTestModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-4">
                    {testLoading ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                      </div>
                    ) : testResult ? (
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">
                            Recipients ({testResult.recipientCount})
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {testResult.recipients?.map((recipient: any, index: number) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                              >
                                {recipient.email} ({recipient.recipientType})
                              </span>
                            ))}
                          </div>
                        </div>

                        {testResult.emailPreview && (
                          <div className="border border-gray-300 rounded-lg overflow-hidden">
                            <div className="bg-gray-100 px-4 py-2">
                              <h4 className="text-sm font-medium text-gray-900">Email Preview</h4>
                              <p className="text-sm text-gray-600">
                                Subject: {testResult.emailPreview.subject}
                              </p>
                            </div>
                            <div className="p-4 bg-white max-h-[50vh] overflow-y-auto">
                              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(testResult.emailPreview.htmlBody) }} />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowTestModal(false)}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDeleteModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Delete Notification Rule
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the rule <strong>{deletingRule?.name}</strong>?
                      This action cannot be undone.
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteModal(false);
                        setDeletingRule(null);
                      }}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default NotificationRulesManagement;
